import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Modal,
  Button,
  Input,
  Select,
  Steps,
  Card,
  Space,
  Tag,
  Badge,
  InputNumber,
  Alert,
  Row,
  Col,
  Divider,
  Checkbox,
  Spin,
} from 'antd';
import LokiBuilder from './LokiBuilder';
import PromQLBuilder from './PromQLBuilder';
import { useTheme } from '../context/ThemeContext';
import { connectorsService } from '../services/connectorsService';
import { INCIDENT_CLIENT_ID } from '../constants/incidentConnectors';
import '../styles/selectFix.css';

const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;

const ClairAIAlertCreator = ({ 
  visible,
  onCancel,
  onSubmit,
  form,
  editing,
  loading,
  dataSource: dataSourceOptions,
  notificationNames,
  titleSuggestions = [],
  titleSuggestionsLoading = false,
  onAlertTitleChange,
  logsService,
  labelOptions,
  clientId,
  awsAccountId,
  expression: parentExpression,
  setExpression: setParentExpression,
  dashboardUid,
  selectDashboardTitle,
  onDashboardSelect,
  alertCapabilities = null,
  initialExtraLabels = null,
  initialTitle = "",
}) => {
  const { isDark } = useTheme();
  const [step, setStep] = useState(0);
  const [sourceData, setSourceData] = useState('');
  // Removed useAmpForMetrics — placement is server-side (automatic).
  const [localExpression, setLocalExpression] = useState('');
  const [correlationExpressions, setCorrelationExpressions] = useState({ prom: '', log: '' });
  const [formData, setFormData] = useState({
    ruleGroup: 'app-alerts',
    title: '',
    description: '',
    severity: '',
    dataSource: '',
    operator: '>',
    threshold: 0,
    relativeFrom: 300,
    relativeTo: 0,
    channels: [],
    includeDashboard: false, 
    viewData: false,
    enableAutoRca: false,
    enableJira: false,
    jiraConnectorId: undefined,
    jiraProjectKey: undefined,
    extraLabels: {},
    noDataState: 'OK',
    execErrState: 'OK',
  });

  // Jira incident-action connectors + projects for the per-alert Jira ticket option
  const [jiraConnectors, setJiraConnectors] = useState([]);
  const [jiraProjects, setJiraProjects] = useState([]);
  const [jiraProjectsLoading, setJiraProjectsLoading] = useState(false);

  // Use parent expression if provided
  const expression = parentExpression !== undefined ? parentExpression : localExpression;
  const setExpression = setParentExpression || setLocalExpression;
  const isCorrelation = formData.dataSource === 'correlation';
  const isMetricsSource = formData.dataSource === 'prometheus';
  const atGrafanaLimit = Boolean(alertCapabilities?.atGrafanaLimit);
  const ampFallbackAvailable = Boolean(alertCapabilities?.canUseAmpForMetrics);
  // Placement is automatic. UI only treats existing AMP edits as AMP (for form fields).
  const isAmpPlatform = editing?.platform === 'amp';
  const resolveAlertPlatform = useCallback(() => (
    editing?.platform === 'amp' ? 'amp' : 'grafana'
  ), [editing]);
  const promExpression = isCorrelation ? correlationExpressions.prom : expression;
  const logExpression = isCorrelation ? correlationExpressions.log : expression;

  // Severity options
  const severities = useMemo(() => [
    { value: 'critical', label: 'Critical', color: 'red' },
    { value: 'warning', label: 'Warning', color: 'orange' },
    { value: 'info', label: 'Info', color: 'blue' }
  ], []);

  const alertStateOptions = useMemo(() => ({
    noData: [
      { value: 'OK', label: 'OK' },
      { value: 'NoData', label: 'No Data' },
      { value: 'Alerting', label: 'Alerting' },
      { value: 'KeepLastState', label: 'Keep Last State' },
    ],
    execErr: [
      { value: 'OK', label: 'OK' },
      { value: 'Error', label: 'Error' },
      { value: 'Alerting', label: 'Alerting' },
      { value: 'KeepLastState', label: 'Keep Last State' },
    ],
  }), []);

  const severityPreviewConfig = useMemo(() => {
    if (!formData.severity) return null;
    const selected = severities.find((severity) => severity.value === formData.severity);
    if (!selected) return null;
    const badgeStatusBySeverity = {
      critical: 'error',
      warning: 'warning',
      info: 'processing',
    };
    const backgroundByColor = {
      red: '#ff4d4f',
      orange: '#fa8c16',
      blue: '#1890ff',
    };

    return {
      ...selected,
      badgeStatus: badgeStatusBySeverity[selected.value] || 'default',
      background: backgroundByColor[selected.color] || '#722ed1',
    };
  }, [formData.severity, severities]);

  const getQueryRule = useCallback((alertData = []) => {
    return alertData.find((item) => item?.model?.expr) || alertData[0] || {};
  }, []);

  const getQueryRuleByType = useCallback((alertData = [], type) => {
    return alertData.find((item) => item?.model?.datasource?.type === type && item?.model?.expr) || {};
  }, []);

  const getDataSourceFromAlertData = useCallback((alertData = [], fallback) => {
    if (fallback) return fallback;

    const hasPrometheus = alertData.some((item) => item?.model?.datasource?.type === 'prometheus' && item?.model?.expr);
    const hasLoki = alertData.some((item) => item?.model?.datasource?.type === 'loki' && item?.model?.expr);
    if (hasPrometheus && hasLoki) return 'correlation';
    if (hasPrometheus) return 'prometheus';
    if (hasLoki) return 'loki';

    return '';
  }, []);

  const getThresholdRule = useCallback((alertData = [], conditionRefId) => {
    const hasThresholdValue = (item) => {
      const params = item?.model?.conditions?.[0]?.evaluator?.params;
      return Array.isArray(params) && params.length > 0;
    };

    return alertData.find((item) => item?.refId === conditionRefId && hasThresholdValue(item)) ||
      alertData.find((item) => item?.model?.refId === conditionRefId && hasThresholdValue(item)) ||
      alertData.find((item) => item?.model?.type === 'threshold' && hasThresholdValue(item)) ||
      alertData.find(hasThresholdValue) ||
      {};
  }, []);

  const getThresholdFromRule = useCallback((alertData = [], fallback) => {
    if (fallback !== undefined && fallback !== null && fallback !== '') {
      return fallback;
    }

    const thresholdRule = getThresholdRule(alertData, editing?.condition);
    const params = thresholdRule?.model?.conditions?.[0]?.evaluator?.params;
    return Array.isArray(params) && params.length > 0 ? params[0] : 0;
  }, [editing?.condition, getThresholdRule]);

  const getOperatorFromRule = useCallback((alertData = [], fallback) => {
    if (fallback) return fallback;

    const evaluatorType = getThresholdRule(alertData, editing?.condition)?.model?.conditions?.[0]?.evaluator?.type;
    const operatorMap = {
      gt: '>',
      gte: '>=',
      lt: '<',
      lte: '<=',
      eq: '==',
    };

    return operatorMap[evaluatorType] || '>';
  }, [editing?.condition, getThresholdRule]);

  // Handlers with useCallback to prevent re-creation
  const handleTitleChange = useCallback((e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, title: value }));
    onAlertTitleChange?.(value);
  }, [onAlertTitleChange]);

  const handleDescriptionChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, description: e.target.value }));
  }, []);

  const handleDataSourceSelect = useCallback((value, prevSource) => {
    if (editing?.platform === 'amp' && value !== 'prometheus') return;
    setFormData((prev) => {
      const next = { ...prev, dataSource: value };
      if (value === 'prometheus' && !next.ruleGroup) {
        next.ruleGroup = 'app-alerts';
      }
      return next;
    });
    setSourceData(value);
    // Clear expression when switching data sources - Prometheus and Loki use different query syntax
    if (prevSource && prevSource !== value) {
      setExpression?.('');
      setCorrelationExpressions({ prom: '', log: '' });
    }
  }, [editing?.platform, setExpression]);

  const handleRuleGroupChange = useCallback((e) => {
    setFormData((prev) => ({ ...prev, ruleGroup: e.target.value }));
  }, []);

  const handlePromExpressionChange = useCallback((expr) => {
    if (isCorrelation) {
      setCorrelationExpressions(prev => ({ ...prev, prom: expr }));
    } else {
      setExpression?.(expr);
    }
  }, [isCorrelation, setExpression]);

  const handleLogExpressionChange = useCallback((expr) => {
    if (isCorrelation) {
      setCorrelationExpressions(prev => ({ ...prev, log: expr }));
    } else {
      setExpression?.(expr);
    }
  }, [isCorrelation, setExpression]);

  const handleOperatorChange = useCallback((val) => {
    setFormData(prev => ({ ...prev, operator: val }));
  }, []);

  const handleThresholdChange = useCallback((val) => {
    setFormData(prev => ({ ...prev, threshold: val ?? 0 }));
  }, []);

  const handleTimeWindowChange = useCallback((val) => {
    setFormData(prev => ({ ...prev, relativeFrom: val }));
  }, []);

  const handleSeverityChange = useCallback((val) => {
    setFormData(prev => ({ ...prev, severity: val }));
    form?.setFieldsValue({ severity: val });
  }, [form]);

  const handleChannelsChange = useCallback((vals) => {
    setFormData(prev => ({ ...prev, channels: vals }));
  }, []);

  const handleChannelRemove = useCallback((channel) => {
    setFormData(prev => ({
      ...prev,
      channels: prev.channels.filter(c => c !== channel)
    }));
  }, []);

  const handleIncludeDashboardChange = useCallback((e) => {
  setFormData(prev => ({ ...prev, includeDashboard: e.target.checked }));
}, []);

const handleViewDataChange = useCallback((e) => {
  setFormData(prev => ({ ...prev, viewData: e.target.checked }));
}, []);

const handleEnableAutoRcaChange = useCallback((e) => {
  setFormData(prev => ({ ...prev, enableAutoRca: e.target.checked }));
}, []);

const loadJiraProjects = useCallback(async (connectorId) => {
  if (!connectorId) {
    setJiraProjects([]);
    return;
  }
  setJiraProjectsLoading(true);
  try {
    const res = await connectorsService.listJiraProjects(connectorId, INCIDENT_CLIENT_ID);
    setJiraProjects(Array.isArray(res?.projects) ? res.projects : []);
  } catch (err) {
    setJiraProjects([]);
  } finally {
    setJiraProjectsLoading(false);
  }
}, []);

const handleEnableJiraChange = useCallback((e) => {
  const checked = e.target.checked;
  setFormData(prev => ({ ...prev, enableJira: checked }));
}, []);

const handleJiraConnectorChange = useCallback((val) => {
  setFormData(prev => ({ ...prev, jiraConnectorId: val, jiraProjectKey: undefined }));
  loadJiraProjects(val);
}, [loadJiraProjects]);

const handleJiraProjectChange = useCallback((val) => {
  setFormData(prev => ({ ...prev, jiraProjectKey: val }));
}, []);

const handleNoDataStateChange = useCallback((val) => {
  setFormData(prev => ({ ...prev, noDataState: val }));
}, []);

const handleExecErrStateChange = useCallback((val) => {
  setFormData(prev => ({ ...prev, execErrState: val }));
}, []);

  const parseAutoRcaFromLabels = useCallback((labels) => {
    const val = String(labels?.auto_rca || '').trim().toLowerCase();
    return ['true', '1', 'yes', 'on'].includes(val);
  }, []);

  const parseJiraFromLabels = useCallback((labels) => {
    const enabledVal = String(labels?.jira_enabled || '').trim().toLowerCase();
    return {
      enableJira: ['true', '1', 'yes', 'on'].includes(enabledVal),
      jiraConnectorId: labels?.jira_connector_id || undefined,
      jiraProjectKey: labels?.jira_project_key || undefined,
    };
  }, []);

  // Load Jira incident-action connectors when the modal opens
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await connectorsService.listInstances({ type: 'jira' }, INCIDENT_CLIENT_ID);
        if (!cancelled) setJiraConnectors(Array.isArray(list) ? list.filter((c) => c.enabled) : []);
      } catch (err) {
        if (!cancelled) setJiraConnectors([]);
      }
    })();
    return () => { cancelled = true; };
  }, [visible]);

  const parseForDurationToSeconds = useCallback((forDuration) => {
    const raw = String(forDuration || "").trim().toLowerCase();
    if (!raw) return 300;
    const match = raw.match(/^(\d+)\s*([smhd])$/);
    if (!match) return 300;
    const value = Number(match[1]);
    const unit = match[2];
    if (unit === "h") return value * 3600;
    if (unit === "d") return value * 86400;
    if (unit === "m") return value * 60;
    return value;
  }, []);

  const resolveSuggestionReceiver = useCallback((payloadReceiver) => {
    const ignored = new Set(["grafana-default-sns", "default", ""]);
    const preferredFromList =
      (notificationNames || []).find((name) => name === "clairai-teams-email") ||
      (notificationNames || []).find((name) => /teams.*email/i.test(String(name))) ||
      (notificationNames || []).find((name) => !ignored.has(String(name || "").trim())) ||
      (notificationNames || [])[0];

    const receiver = String(payloadReceiver || "").trim();
    if (receiver && !ignored.has(receiver)) {
      return [receiver];
    }
    if (preferredFromList) {
      return [preferredFromList];
    }
    return formData.channels || [];
  }, [notificationNames, formData.channels]);

  const handleUseTitleSuggestion = useCallback((suggestion) => {
    const payload = suggestion?.create_payload || {};
    const thresholdOpMap = { gt: ">", gte: ">=", lt: "<", lte: "<=", eq: "==" };
    const resolvedOperator = thresholdOpMap[payload.threshold_op] || payload.threshold_op || ">";
    const resolvedChannels = resolveSuggestionReceiver(payload.receiver);
    const resolvedDescription = payload.description || formData.description || "";
    const resolvedPromExpr = payload.prom_expr || payload.log_expr || expression || "";
    const resolvedDataSource =
      editing?.platform === "amp"
        ? "prometheus"
        : (
          payload.datasource ||
          (payload.prom_expr && payload.log_expr
            ? "correlation"
            : payload.prom_expr
              ? "prometheus"
              : payload.log_expr
                ? "loki"
                : formData.dataSource)
        );
    // New creates use Grafana API; server may place metrics in AMP automatically.
    // AMP form path only when editing an existing AMP alert.
    const isAmp = editing?.platform === 'amp';

    if (isAmp) {
      const suggestionTitle = String(payload.title || suggestion?.title || formData.title || "").trim();
      const ampTitle = suggestionTitle.includes("·")
        ? suggestionTitle.split("·").pop().trim()
        : suggestionTitle;
      const nextValues = {
        title: ampTitle || formData.title,
        description: resolvedDescription,
        dataSource: "prometheus",
        ruleGroup: formData.ruleGroup || "app-alerts",
        severity: payload.severity || formData.severity || "warning",
        operator: resolvedOperator,
        threshold: payload.threshold_value ?? formData.threshold ?? 0,
        relativeFrom: parseForDurationToSeconds(payload.for_duration) || formData.relativeFrom || 300,
        channels: resolvedChannels,
        extraLabels: {
          ...(formData.extraLabels || {}),
          ...(payload.extra_labels || {}),
        },
      };

      setFormData((prev) => ({ ...prev, ...nextValues }));
      setSourceData("prometheus");
      setExpression?.(resolvedPromExpr);

      if (form) {
        form.setFieldsValue({
          title: nextValues.title,
          description: nextValues.description,
          dataSource: "prometheus",
          alertPlatform: "amp",
          ruleGroup: nextValues.ruleGroup,
          severity: nextValues.severity,
          operator: nextValues.operator,
          threshold: nextValues.threshold,
          relativeFrom: nextValues.relativeFrom,
          relativeTo: formData.relativeTo ?? 0,
          notificationChannels: nextValues.channels,
        });
      }
      return;
    }

    const resolvedExpression =
      resolvedDataSource === "loki"
        ? (payload.log_expr || payload.prom_expr || expression || "")
        : (payload.prom_expr || payload.log_expr || expression || "");
    const nextValues = {
      title: payload.title || formData.title,
      description: payload.description || formData.description || '',
      dataSource: resolvedDataSource || "",
      severity: payload.severity || formData.severity || '',
      operator: payload.threshold_op || formData.operator || ">",
      threshold: payload.threshold_value ?? formData.threshold ?? 0,
      relativeFrom: parseForDurationToSeconds(payload.for_duration) || formData.relativeFrom || 300,
      channels: resolvedChannels,
      includeDashboard: formData.includeDashboard || false,
      viewData: formData.viewData || false,
      extraLabels: {
        ...(formData.extraLabels || {}),
        ...(payload.extra_labels || {}),
      },
    };

    setFormData((prev) => ({ ...prev, ...nextValues }));
    setSourceData(nextValues.dataSource);
    setExpression?.(resolvedExpression);

    if (form) {
      form.setFieldsValue({
        title: nextValues.title,
        dataSource: nextValues.dataSource,
        alertPlatform: "grafana",
        severity: nextValues.severity,
        operator: nextValues.operator,
        threshold: nextValues.threshold,
        relativeFrom: nextValues.relativeFrom,
        relativeTo: formData.relativeTo ?? 0,
        notificationChannels: nextValues.channels,
      });
    }
  }, [formData, expression, form, parseForDurationToSeconds, setExpression, resolveSuggestionReceiver, editing?.platform]);

  // Reset step when modal opens
  useEffect(() => {
    if (visible) {
      setStep(0);
      // Reset form data when opening for new alert
      if (!editing) {
        const seededLabels =
          initialExtraLabels && typeof initialExtraLabels === "object"
            ? { ...initialExtraLabels }
            : {};
        setFormData({
          ruleGroup: 'app-alerts',
          title: initialTitle || '',
          description: '',
          severity: '',
          dataSource: '',
          operator: '>',
          threshold: 0,
          relativeFrom: 300,
          relativeTo: 0,
          channels: [],
          includeDashboard: false,
          viewData: false,
          enableAutoRca: false,
          enableJira: false,
          jiraConnectorId: undefined,
          jiraProjectKey: undefined,
          extraLabels: seededLabels,
          noDataState: 'OK',
          execErrState: 'OK',
        });
        setJiraProjects([]);
        setSourceData('');
        setCorrelationExpressions({ prom: '', log: '' });
        if (form) {
          form.setFieldsValue({
            title: initialTitle || undefined,
            extraLabels: seededLabels,
          });
        }
      }
    }
  }, [visible, editing, initialExtraLabels, initialTitle, form]);

  // When opening Edit Alert: pre-fill from editing record so title and all options show immediately
  useEffect(() => {
    if (!visible || !editing) return;

    if (editing.platform === 'amp') {
      const channels = Array.isArray(editing.notificationChannels)
        ? editing.notificationChannels
        : (editing.notificationChannels ? [editing.notificationChannels] : []);
      const savedDescription = editing.annotations?.description || editing.annotations?.summary || '';
      const savedExpr = editing.metricExpression || editing.expression || '';

      setExpression?.(savedExpr);
      setCorrelationExpressions({ prom: '', log: '' });
      setSourceData('prometheus');

      setFormData((prev) => ({
        ...prev,
        ruleGroup: editing.ruleGroup || 'app-alerts',
        title: editing.title ?? prev.title,
        description: savedDescription || prev.description,
        dataSource: 'prometheus',
        severity: editing.severity || prev.severity,
        operator: editing.operator || prev.operator || '>',
        threshold: editing.threshold_value ?? prev.threshold ?? 0,
        relativeFrom: editing.relativeFrom ?? prev.relativeFrom ?? 300,
        relativeTo: editing.relativeTo ?? prev.relativeTo ?? 0,
        channels: channels.length ? channels : prev.channels,
        includeDashboard: editing.viewDashboard != null && editing.viewDashboard !== '',
        viewData: editing.viewData != null && editing.viewData !== '',
        enableAutoRca: parseAutoRcaFromLabels(editing.labels),
        ...parseJiraFromLabels(editing.labels),
        extraLabels: { ...(editing.labels || {}) },
      }));

      {
        const jiraPrefill = parseJiraFromLabels(editing.labels);
        if (jiraPrefill.enableJira && jiraPrefill.jiraConnectorId) {
          loadJiraProjects(jiraPrefill.jiraConnectorId);
        }
      }

      if (form) {
        form.setFieldsValue({
          title: editing.title,
          description: savedDescription,
          dataSource: 'prometheus',
          alertPlatform: 'amp',
          ruleGroup: editing.ruleGroup || 'app-alerts',
          notificationChannels: channels,
          relativeFrom: editing.relativeFrom ?? 300,
          threshold: editing.threshold_value ?? 0,
          includeDashboard: editing.viewDashboard != null && editing.viewDashboard !== '',
          viewData: editing.viewData != null && editing.viewData !== '',
          severity: editing.severity || 'warning',
          operator: editing.operator || '>',
        });
      }
      return;
    }

    const alertData = editing.data || [];
    const r = getQueryRule(alertData);
    const ds = getDataSourceFromAlertData(alertData, editing.dataSource);
    const promRule = getQueryRuleByType(alertData, 'prometheus');
    const logRule = getQueryRuleByType(alertData, 'loki');
    const channels = Array.isArray(editing.notificationChannels)
      ? editing.notificationChannels
      : (editing.notificationChannels ? [editing.notificationChannels] : []);
    const threshold = getThresholdFromRule(alertData, editing.threshold_value);
    const operator = getOperatorFromRule(alertData, editing.operator);
    
    // Set the expression from the saved alert (r.model?.expr holds the query)
    const savedPromExpr = promRule?.model?.expr || '';
    const savedLogExpr = logRule?.model?.expr || '';
    const savedExpr = ds === 'correlation' ? (savedPromExpr || savedLogExpr) : (r.model?.expr || '');
    const savedDescription =
      editing.annotations?.description ||
      alertData.find((item) => item?.annotations?.description)?.annotations?.description ||
      '';
    setExpression?.(savedExpr);
    setCorrelationExpressions({ prom: savedPromExpr, log: savedLogExpr });
    
    setFormData(prev => ({
      ...prev,
      ruleGroup: editing.ruleGroup || prev.ruleGroup || 'app-alerts',
      title: editing.title ?? prev.title,
      description: savedDescription || prev.description,
      dataSource: ds || prev.dataSource,
      severity: editing.severity || prev.severity,
      operator,
      threshold,
      relativeFrom: editing.relativeFrom ?? r.relativeTimeRange?.from ?? prev.relativeFrom,
      relativeTo: editing.relativeTo ?? r.relativeTimeRange?.to ?? prev.relativeTo,
      channels: channels.length ? channels : prev.channels,
      includeDashboard: editing.viewDashboard != null && editing.viewDashboard !== '',
      viewData: editing.viewData != null && editing.viewData !== '',
      enableAutoRca: parseAutoRcaFromLabels(editing.labels),
      ...parseJiraFromLabels(editing.labels),
      extraLabels: { ...(editing.labels || {}) },
      noDataState: editing.noDataState || editing.no_data_state || 'OK',
      execErrState: editing.execErrState || editing.exec_err_state || 'OK',
    }));
    {
      const jiraPrefill = parseJiraFromLabels(editing.labels);
      if (jiraPrefill.enableJira && jiraPrefill.jiraConnectorId) {
        loadJiraProjects(jiraPrefill.jiraConnectorId);
      }
    }
    setSourceData(ds || '');
    if (form) {
      form.setFieldsValue({
        title: editing.title,
        description: savedDescription,
        dataSource: ds,
        notificationChannels: channels,
        relativeFrom: editing.relativeFrom ?? r.relativeTimeRange?.from ?? 300,
        relativeTo: editing.relativeTo ?? r.relativeTimeRange?.to ?? 0,
        threshold,
        severity: editing.severity || 'warning',
        operator,
        prom_expr: savedPromExpr || savedExpr,
        log_expr: savedLogExpr || savedExpr,
        includeDashboard: editing.viewDashboard != null && editing.viewDashboard !== '',
        viewData: editing.viewData != null && editing.viewData !== '',
        enableAutoRca: parseAutoRcaFromLabels(editing.labels),
      });
    }
  }, [visible, editing, form, getDataSourceFromAlertData, getOperatorFromRule, getQueryRule, getQueryRuleByType, getThresholdFromRule, setExpression, parseAutoRcaFromLabels, parseJiraFromLabels, loadJiraProjects]);

  const hasQueryForStep = () => {
    if (isCorrelation) {
      return Boolean(correlationExpressions.prom?.trim() && correlationExpressions.log?.trim());
    }
    return Boolean(String(expression || '').trim() && expression !== '{}');
  };

  // Soft UX hint only — create is still allowed; backend placement/errors are authoritative.
  const showLimitHint = !editing && atGrafanaLimit && formData.dataSource;

  const canProceed = () => {
    switch(step) {
      case 0:
        return Boolean(
          formData.title &&
          formData.dataSource &&
          (!isAmpPlatform || formData.ruleGroup?.trim()),
        );
      case 1: return hasQueryForStep();
      case 2: return formData.severity && formData.channels.length > 0;
      default: return true;
    }
  };

  const handleNext = () => {
    if (canProceed()) {
      setStep(step + 1);
    }
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const handleSubmit = () => {
    // Build labels: strip any previous Jira labels, then re-apply from the current selection.
    const extraLabels = { ...(formData.extraLabels || {}) };
    delete extraLabels.jira_enabled;
    delete extraLabels.jira_connector_id;
    delete extraLabels.jira_project_key;
    delete extraLabels.jira_client_id;
    if (formData.enableJira && formData.jiraConnectorId) {
      extraLabels.jira_enabled = 'true';
      extraLabels.jira_connector_id = formData.jiraConnectorId;
      if (formData.jiraProjectKey) extraLabels.jira_project_key = formData.jiraProjectKey;
      extraLabels.jira_client_id = INCIDENT_CLIENT_ID;
    }

    const submitData = {
      alertPlatform: resolveAlertPlatform(),
      ruleGroup: formData.ruleGroup,
      title: formData.title,
      description: formData.description?.trim() || '',
      dataSource: formData.dataSource,
      expression: isCorrelation ? correlationExpressions.prom : expression,
      promExpression: isCorrelation ? correlationExpressions.prom : expression,
      logExpression: isCorrelation ? correlationExpressions.log : expression,
      notificationChannels: formData.channels.length ? formData.channels : [],
      relativeFrom: formData.relativeFrom,
      relativeTo: formData.relativeTo,
      severity: formData.severity,
      operator: formData.operator, 
      threshold: formData.threshold,
      includeDashboard: formData.includeDashboard,
      viewData: formData.viewData,
      enableAutoRca: formData.enableAutoRca,
      enableJira: formData.enableJira,
      jiraConnectorId: formData.jiraConnectorId,
      jiraProjectKey: formData.jiraProjectKey,
      extraLabels,
      noDataState: formData.noDataState,
      execErrState: formData.execErrState,
      dashboard_uid: selectDashboardTitle,
    };

    if (form) {
      form.setFieldsValue(submitData);
    }

    if (onSubmit) {
      onSubmit(submitData);
    }
  };

  // Step 1: Source Selection
 const StepOne = useMemo(() => {
    const showTitleSuggestions = String(formData.title || "").trim().length >= 3;
    // AMP edits stay on metrics only; create shows all sources and routes by type.
    const visibleDataSources = (dataSourceOptions || []).filter(
      (source) => editing?.platform !== 'amp' || source.value === 'prometheus',
    );
    return (
      <div style={{ padding: '20px 0' }}>
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: isDark ? '#e6edf3' : undefined }}>
            <span style={{ color: 'red' }}>* </span>Alert Title
          </label>
          <Input
            size="large"
            value={formData.title}
            onChange={handleTitleChange}
            placeholder="e.g., High CPU Usage on Production Servers"/>
          <div style={{ marginTop: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: isDark ? '#e6edf3' : undefined }}>
              Description <span style={{ fontWeight: 400, color: isDark ? '#8b949e' : '#8c8c8c' }}>(optional)</span>
            </label>
            <TextArea
              value={formData.description}
              onChange={handleDescriptionChange}
              placeholder="Add context for this alert — what it monitors and when it should fire"
              rows={3}
              maxLength={500}
              showCount
            />
          </div>
          {showTitleSuggestions && (
            <div style={{ marginTop: 12 }}>
              <Card
                size="small"
                style={{
                  borderRadius: 10,
                  border: isDark ? '1px solid #30363d' : '1px solid #d9e7ff',
                  background: isDark ? '#161b22' : '#f8fbff',
                }}
                bodyStyle={{ padding: 12 }}
              >
                <div style={{ marginBottom: 8, fontWeight: 600, color: isDark ? '#e6edf3' : '#1d39c4' }}>
                  Intelligent Suggestions
                </div>
                {titleSuggestionsLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      fontSize: 12,
                      color: isDark ? '#8b949e' : '#595959',
                    }}
                  >
                    <Spin size="small" />
                    <span>Loading suggestions for this title...</span>
                  </div>
                ) : titleSuggestions.length > 0 ? (
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {titleSuggestions.map((item) => (
                      <div
                        key={item.suggestion_id || item.title}
                        style={{
                          border: isDark ? '1px solid #30363d' : '1px solid #d9d9d9',
                          borderRadius: 8,
                          padding: 10,
                          background: isDark ? '#0d1117' : '#fff',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: isDark ? '#e6edf3' : '#262626' }}>{item.title}</div>
                            {item.description && (
                              <div style={{ marginTop: 4, fontSize: 12, color: isDark ? '#8b949e' : '#595959' }}>
                                {item.description}
                              </div>
                            )}
                          </div>
                          <Button size="small" type="default" onClick={() => handleUseTitleSuggestion(item)}>
                            Use Suggestion
                          </Button>
                        </div>
                      </div>
                    ))}
                  </Space>
                ) : (
                  <div style={{ fontSize: 12, color: isDark ? '#8b949e' : '#595959' }}>
                    No matching suggestions. Continue with manual configuration.
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>

      <div>
        <label style={{ display: 'block', marginBottom: 12, fontWeight: 500, color: isDark ? '#e6edf3' : undefined }}>
          <span style={{ color: 'red' }}>* </span>Data Source
        </label>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {visibleDataSources.map(source => (
            <Card
              key={source.value}
              hoverable={!editing || editing.platform !== 'amp'}
              onClick={() => {
                if (editing?.platform === 'amp') return;
                handleDataSourceSelect(source.value, formData.dataSource);
              }}
              style={{
                border: formData.dataSource === source.value ? '2px solid #1890ff' : (isDark ? '1px solid #30363d' : '1px solid #d9d9d9'),
                background: formData.dataSource === source.value
                  ? (isDark ? '#111d2c' : '#e6f7ff')
                  : (isDark ? '#161b22' : '#fff'),
                opacity: editing?.platform === 'amp' ? 0.85 : 1,
                cursor: editing?.platform === 'amp' ? 'not-allowed' : 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  background: formData.dataSource === source.value ? '#1890ff' : (isDark ? '#21262d' : '#f0f0f0'),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 16, color: isDark ? '#e6edf3' : '#000' }}>{source.name}</div>
                  <div style={{ color: isDark ? '#8b949e' : '#8c8c8c', fontSize: 14 }}>
                    {source.value.includes('prometheus') ? 'Monitor metric thresholds with PromQL' :
                     source.value.includes('loki') ? 'Monitor log patterns and errors' :
                     'Cross-signal analysis across metrics and logs'}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </Space>
      </div>

      {showLimitHint && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 24 }}
          message={`Grafana alert rule limit reached (${alertCapabilities?.grafanaRuleCount ?? 100}/${alertCapabilities?.grafanaRuleLimit ?? 100})`}
          description={
            isMetricsSource && ampFallbackAvailable
              ? 'New metric alerts will be placed in AMP automatically. Log and correlation alerts require freeing Grafana capacity first.'
              : isMetricsSource
                ? 'New metric alerts cannot be created until Grafana capacity is freed or an administrator enables AMP.'
                : 'Log and correlation alerts cannot be created until you free Grafana capacity by removing unused rules.'
          }
        />
      )}

      {isAmpPlatform && (
        <div style={{ marginTop: 24 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: isDark ? '#e6edf3' : undefined }}>
            <span style={{ color: 'red' }}>* </span>Rule Group
          </label>
          <Input
            size="large"
            value={formData.ruleGroup}
            onChange={handleRuleGroupChange}
            placeholder="e.g., app-alerts"
            disabled={Boolean(editing)}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: isDark ? '#8b949e' : '#8c8c8c' }}>
            Existing AMP rule group (read-only provenance for this alert).
          </div>
        </div>
      )}
    </div>
  );
}, [
  formData.title,
  formData.description,
  formData.dataSource,
  formData.ruleGroup,
  isAmpPlatform,
  editing,
  atGrafanaLimit,
  isMetricsSource,
  ampFallbackAvailable,
  showLimitHint,
  alertCapabilities,
  handleRuleGroupChange,
  handleTitleChange,
  handleDescriptionChange,
  handleDataSourceSelect,
  handleUseTitleSuggestion,
  titleSuggestions,
  titleSuggestionsLoading,
  dataSourceOptions,
  isDark,
]);

  // Step 2: Condition
const StepTwo = useMemo(() => (
    <div style={{ padding: '20px 0' }}>
      {/* Query Builder */}
      {(sourceData?.includes('prometheus') || sourceData?.includes('correlation')) && (
        <div style={{ 
          background: isDark ? 'linear-gradient(to bottom right, #111d2c, #0d1520)' : 'linear-gradient(to bottom right, #e6f7ff, #bae7ff)', 
          padding: 20, 
          borderRadius: 8,
          marginBottom: 24,
          border: isDark ? '1px solid #30363d' : '1px solid #91d5ff',
          minHeight: '400px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            
            <span style={{ fontWeight: 600, color: isDark ? '#e6edf3' : undefined }}>PromQL Query</span>
          </div>
          {PromQLBuilder && (
            <PromQLBuilder 
              value={promExpression}
              onChange={handlePromExpressionChange}
              isDark={isDark}
            />
          )}
        </div>
      )}

      {(sourceData?.includes('loki') || sourceData?.includes('correlation')) && (
        <div style={{ 
          background: isDark ? 'linear-gradient(to bottom right, #0d1f0d, #0a1a0a)' : 'linear-gradient(to bottom right, #f6ffed, #d9f7be)', 
          padding: 20, 
          borderRadius: 8,
          marginBottom: 24,
          border: isDark ? '1px solid #238636' : '1px solid #b7eb8f'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            
            <span style={{ fontWeight: 600, color: isDark ? '#e6edf3' : undefined }}>LogQL Query</span>
          </div>
          {LokiBuilder && logsService && labelOptions && (
            <LokiBuilder
              labelKeys={logsService}
              valueOptions={labelOptions}
              value={logExpression}
              onChange={handleLogExpressionChange}
              isDark={isDark}
              clientId={clientId}
              awsAccountId={awsAccountId}
            />
          )}
        </div>
      )}

      {/* Threshold Configuration */}
      <Card style={{ background: isDark ? '#161b22' : '#fafafa', border: isDark ? '1px solid #30363d' : undefined }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 500, display: 'block', marginBottom: 8, color: isDark ? '#e6edf3' : '#000'  }}>
            Trigger Condition <span style={{ fontWeight: 400, color: isDark ? '#8b949e' : '#8c8c8c' }}>(optional)</span>
          </label>
          <div style={{ fontSize: 12, color: isDark ? '#8b949e' : '#8c8c8c', marginBottom: 12 }}>
            Defaults to greater than 0. Override here, or put the full condition directly in your query above.
          </div>
        </div>
        
        <Space wrap>
          <span style={{ color: isDark ? '#8b949e' : '#595959' }}>When metric is</span>
          <Select
            value={formData.operator}
            onChange={handleOperatorChange}
            style={{ width: 150 }}
            dropdownClassName="black-options"
          >
            <Option value=">">greater than (&gt;)</Option>
            <Option value=">=">greater or equal (≥)</Option>
            <Option value="<">less than (&lt;)</Option>
            <Option value="<=">less or equal (≤)</Option>
            <Option value="==">equals (=)</Option>
          </Select>

          <InputNumber
            value={formData.threshold}
            onChange={handleThresholdChange}
            placeholder="Value"
            style={{ width: 120 }}
          />

          <span style={{ color: isDark ? '#8b949e' : '#595959' }}>for</span>

          <Select
            value={formData.relativeFrom}
            onChange={handleTimeWindowChange}
            style={{ width: 130 }}
            dropdownClassName="black-options"
          >
            <Option value={60}>1 minute</Option>
            <Option value={300}>5 minutes</Option>
            <Option value={900}>15 minutes</Option>
            <Option value={1800}>30 minutes</Option>
            <Option value={3600}>1 hour</Option>
          </Select>
        </Space>

        {formData.threshold !== '' && formData.threshold !== undefined && formData.threshold !== null && (
          <Alert
            message={
              <span>
                
                AI Insight: Based on historical data, this would trigger ~3x/day
              </span>
            }
            type="info"
            style={{ marginTop: 16 }}
          />
        )}
      </Card>
    </div>
),[sourceData, promExpression, logExpression, handlePromExpressionChange, handleLogExpressionChange, formData.operator, formData.threshold, formData.relativeFrom, handleOperatorChange, handleThresholdChange, handleTimeWindowChange, logsService, labelOptions, isDark, clientId, awsAccountId]);

  // Step 3: Notifications
const StepThree = useMemo(() => (
  <div style={{ padding: '20px 0' }}>
    <div style={{ marginBottom: 24 }}>
      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: isDark ? '#e6edf3' : undefined }}>
        <span style={{ color: 'red' }}>* </span>Severity Level
      </label>
      <Select
        size="large"
        value={formData.severity || undefined}
        onChange={handleSeverityChange}
        placeholder="Select severity level"
        style={{ width: '100%' }}
      >
        {severities.map(sev => (
          <Option key={sev.value} value={sev.value}>
            <Badge color={sev.color} text={sev.label} />
          </Option>
        ))}
      </Select>
    </div>

    <div>
      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: isDark ? '#e6edf3' : undefined }}>
        <span style={{ color: 'red' }}>* </span>Notification Channels
      </label>
      <Select
        mode="multiple"
        size="large"
        value={formData.channels}
        onChange={handleChannelsChange}
        placeholder="Select notification channels"
        style={{ width: '100%' }}>
        {notificationNames.map(name => (
          <Option key={name} value={name}>{name}</Option>
        ))}
      </Select>
      {formData.channels.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {formData.channels.map(channel => (
            <Tag 
              key={channel} 
              color="blue" 
              closable
              onClose={() => handleChannelRemove(channel)}
              style={{ marginBottom: 8 }}
            >
              {channel}
            </Tag>
          ))}
        </div>
      )}
    </div>
      {!isAmpPlatform && (
      <div style={{ marginTop: 24, marginBottom: 24 }}>
        <label style={{ display: 'block', marginBottom: 12, fontWeight: 500, color: isDark ? '#e6edf3' : undefined }}>
          Configure no data and error handling
        </label>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, color: isDark ? '#8b949e' : '#595959' }}>
            Alert state if no data or all values are null
          </label>
          <Select
            value={formData.noDataState}
            onChange={handleNoDataStateChange}
            style={{ width: '100%' }}
            dropdownClassName="black-options"
          >
            {alertStateOptions.noData.map((opt) => (
              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
            ))}
          </Select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 8, color: isDark ? '#8b949e' : '#595959' }}>
            Alert state if execution error or timeout
          </label>
          <Select
            value={formData.execErrState}
            onChange={handleExecErrStateChange}
            style={{ width: '100%' }}
            dropdownClassName="black-options"
          >
            {alertStateOptions.execErr.map((opt) => (
              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
            ))}
          </Select>
        </div>
      </div>
      )}

      <>
        <div style={{ marginTop: 24 }}>
          <Checkbox
            checked={formData.includeDashboard}
            onChange={handleIncludeDashboardChange}
          >
            Include Dashboard Link
          </Checkbox>
        </div>

        {formData.includeDashboard && (
          <div style={{ marginTop: 12, marginLeft: 24 }}>
            <Select
              placeholder="Select Dashboard"
              value={selectDashboardTitle || undefined}
              onChange={onDashboardSelect}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
              loading={loading}
            >
              {selectDashboardTitle &&
                !(dashboardUid || []).some((dash) => dash.uid === selectDashboardTitle) && (
                  <Option key={selectDashboardTitle} value={selectDashboardTitle}>
                    {selectDashboardTitle}
                  </Option>
                )}
              {(dashboardUid || []).map(dash => (
                <Option key={dash.uid} value={dash.uid}>{dash.title}</Option>
              ))}
            </Select>
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <Checkbox
            checked={formData.viewData}
            onChange={handleViewDataChange}
          >
            Include View Data Link
          </Checkbox>
        </div>
      </>

    <div style={{ marginTop: 12 }}>
      <Checkbox
        checked={formData.enableAutoRca}
        onChange={handleEnableAutoRcaChange}
      >
        Enable Auto-RCA when alert fires
      </Checkbox>
      {isAmpPlatform && (
        <div style={{ marginTop: 4, fontSize: 12, color: isDark ? '#8b949e' : '#8c8c8c' }}>
          Adds <code>auto_rca=true</code> label on the AMP rule for webhook-triggered RCA.
        </div>
      )}
    </div>

    <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${isDark ? '#30363d' : '#f0f0f0'}` }}>
      <Checkbox
        checked={formData.enableJira}
        onChange={handleEnableJiraChange}
      >
        Create Jira ticket when this alert fires
      </Checkbox>
      <div style={{ marginTop: 4, fontSize: 12, color: isDark ? '#8b949e' : '#8c8c8c' }}>
        After RCA completes, an incident ticket is created in the selected Jira project.
      </div>

      {formData.enableJira && (
        <div style={{ marginTop: 12, marginLeft: 24 }}>
          <div style={{ marginBottom: 8, color: isDark ? '#8b949e' : '#595959' }}>Jira connector</div>
          <Select
            placeholder={jiraConnectors.length ? 'Select Jira connector' : 'No Jira connectors configured'}
            value={formData.jiraConnectorId}
            onChange={handleJiraConnectorChange}
            style={{ width: '100%', marginBottom: 12 }}
            dropdownClassName="black-options"
            notFoundContent="Add a Jira connector under Connectors → Incident actions"
          >
            {jiraConnectors.map((c) => (
              <Option key={c.connector_id} value={c.connector_id}>{c.name}</Option>
            ))}
          </Select>

          <div style={{ marginBottom: 8, color: isDark ? '#8b949e' : '#595959' }}>Jira project</div>
          <Select
            placeholder={formData.jiraConnectorId ? 'Select project' : 'Select a connector first'}
            value={formData.jiraProjectKey}
            onChange={handleJiraProjectChange}
            style={{ width: '100%' }}
            disabled={!formData.jiraConnectorId}
            loading={jiraProjectsLoading}
            showSearch
            optionFilterProp="children"
            dropdownClassName="black-options"
          >
            {jiraProjects.map((p) => (
              <Option key={p.key} value={p.key}>{p.name ? `${p.name} (${p.key})` : p.key}</Option>
            ))}
          </Select>
        </div>
      )}
    </div>
  </div>
), [formData.severity, formData.channels, formData.includeDashboard, formData.viewData, formData.enableAutoRca, formData.enableJira, formData.jiraConnectorId, formData.jiraProjectKey, jiraConnectors, jiraProjects, jiraProjectsLoading, handleEnableJiraChange, handleJiraConnectorChange, handleJiraProjectChange, formData.noDataState, formData.execErrState, isAmpPlatform, handleSeverityChange, handleChannelsChange, handleIncludeDashboardChange, handleViewDataChange, handleEnableAutoRcaChange, handleNoDataStateChange, handleExecErrStateChange, handleChannelRemove, notificationNames, severities, alertStateOptions, selectDashboardTitle, onDashboardSelect, dashboardUid, loading, isDark]);
  // Preview Panel
  const PreviewPanel = () => {
    const selectedSource = dataSourceOptions.find(d => d.value === formData.dataSource);
    const generatedQueries = isCorrelation
      ? [
          { label: 'PromQL Query', value: correlationExpressions.prom },
          { label: 'LogQL Query', value: correlationExpressions.log },
        ].filter((item) => item.value)
      : [{ label: 'Generated Query', value: expression }].filter((item) => item.value);

    return (
      <Card 
        title={
          <span style={{ color: isDark ? '#e6edf3' : undefined }}>
            
            Alert Preview
          </span>
        }
        style={{ height: '100%', background: isDark ? '#161b22' : undefined, borderColor: isDark ? '#30363d' : undefined }}
      >
        <Card size="small" style={{ marginBottom: 16, background: isDark ? '#21262d' : undefined, borderColor: isDark ? '#30363d' : undefined }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: 12 }}>
            {severityPreviewConfig ? (
              <Badge status={severityPreviewConfig.badgeStatus} />
            ) : (
              <Badge status="default" />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {formData.title || 'Untitled Alert'}
              </div>
              {formData.description && (
                <div style={{ fontSize: 13, color: isDark ? '#8b949e' : '#8c8c8c', marginBottom: 4 }}>
                  {formData.description}
                </div>
              )}
              {formData.threshold !== '' && formData.threshold !== undefined && formData.threshold !== null && (
                <div style={{ fontSize: 13, color: '#8c8c8c' }}>
                  Value {formData.operator} {formData.threshold} for {formData.relativeFrom}s
                </div>
              )}
              <Space wrap size={[8, 8]} style={{ marginTop: 8, gap: 8 }} align="center">
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 12px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 500,
                    background: isAmpPlatform ? '#fa541c' : '#597ef7',
                    color: '#fff',
                  }}
                >
                  {isAmpPlatform ? 'AMP' : 'Grafana'}
                </span>
                {isAmpPlatform && formData.ruleGroup && (
                  <Tag color="volcano">{formData.ruleGroup}</Tag>
                )}
                {selectedSource && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 12px',
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 500,
                      background: formData.dataSource?.includes('correlation') ? '#722ed1' :
                        formData.dataSource?.includes('prometheus') ? '#1890ff' :
                        formData.dataSource?.includes('loki') ? '#52c41a' : (isDark ? '#21262d' : '#f0f0f0'),
                      color: '#fff',
                      border: 'none',
                    }}
                  >
                    {selectedSource.name}
                  </span>
                )}
                {severityPreviewConfig && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 12px',
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 500,
                      background: severityPreviewConfig.background,
                      color: '#fff',
                      border: 'none',
                    }}
                  >
                    {severityPreviewConfig.label}
                  </span>
                )}
              </Space>
            </div>
          </div>
        </Card>

        {formData.channels.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: isDark ? '#8b949e' : '#8c8c8c', marginBottom: 8 }}>
              Notifications:
            </div>
            <Space wrap>
              {formData.channels.map(channel => (
                <Tag key={channel} color="blue" style={{ fontSize: 11 }}>
                  {channel}
                </Tag>
              ))}
            </Space>
          </div>
        )}

        <Divider />

{/* Generated Query Display */}
{generatedQueries.length > 0 && (
  <>
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: isDark ? '#8b949e' : '#8c8c8c', marginBottom: 8 }}>
        {isCorrelation ? 'Generated Queries' : 'Generated Query'}
      </div>
      {generatedQueries.map((item) => (
        <div key={item.label} style={{ marginBottom: 8 }}>
          {isCorrelation && (
            <div style={{ fontSize: 11, color: isDark ? '#8b949e' : '#8c8c8c', marginBottom: 4 }}>
              {item.label}
            </div>
          )}
          <div style={{
            background: isDark ? '#0d1117' : '#f5f5f5',
            padding: 12,
            borderRadius: 6,
            fontFamily: 'Monaco, Consolas, monospace',
            fontSize: 12,
            color: isDark ? '#e6edf3' : '#262626',
            wordBreak: 'break-word',
            maxHeight: 120,
            overflowY: 'auto',
            border: isDark ? '1px solid #30363d' : '1px solid #d9d9d9'
          }}>
            {item.value}
          </div>
        </div>
      ))}
    </div>
    <Divider />
  </>
)}
        {/* Sample Chart */}
        <div>
          <div style={{ fontSize: 12, color: isDark ? '#8b949e' : '#8c8c8c', marginBottom: 12 }}>
            Sample metric trend
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 60 }}>
            {[35, 42, 38, 55, 48, 72, 85, 65, 58, 90, 78, 62].map((h, i) => (
              <div 
                key={i} 
                style={{ 
                  flex: 1, 
                  height: `${h}%`, 
                  background: formData.threshold !== '' && formData.threshold !== undefined && formData.threshold !== null && h > parseFloat(formData.threshold) ? '#ff4d4f' : '#1890ff',
                  borderRadius: 2,
                }}
              />
            ))}
          </div>
        </div>

        <Divider />

        {/* AI Feature */}
        <Alert
          message="AI-Powered Analysis"
          description="ClairAI will automatically analyze correlated events to identify root causes when this alert triggers."
          type="info"/>
      </Card>
    );
  };

  return (
    <Modal
      key="alert-creator-modal"
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, color: isDark ? '#e6edf3' : undefined }}>
              {editing ? 'Edit Alert' : 'Create Alert'}
            </div>
            <div style={{ fontSize: 13, fontWeight: 400, color: isDark ? '#8b949e' : '#8c8c8c' }}>
              Configure intelligent monitoring for your infrastructure
            </div>
          </div>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={1200}
      footer={null}
      destroyOnClose={true}
      maskClosable={false}
      styles={isDark ? { content: { background: '#0d1117' }, body: { background: '#0d1117' }, header: { background: '#0d1117', borderColor: '#30363d' } } : undefined}
    >
      <div style={{ marginBottom: 24 }}>
        <Steps current={step} size="small">
          <Step title="Source"/>
          <Step title="Condition"/>
          <Step title="Notify"/>
        </Steps>
      </div>

      <Row gutter={24}>
        <Col span={16}>
          {/* {step === 0 && <StepOne />}
          {step === 1 && <StepTwo />}
          {step === 2 && <StepThree />} */}
          {step === 0 && StepOne}
          {step === 1 && StepTwo}
          {step === 2 && StepThree}
        </Col>
        <Col span={8}>
          <PreviewPanel />
        </Col>
      </Row>

      <Divider />

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Space>
          {step > 0 && (
            <Button onClick={handlePrev}>
              Back
            </Button>
          )}
          {step < 2 ? (
            <Button 
              type="primary" 
              onClick={handleNext}
              disabled={!canProceed()}>
              Continue
            </Button>
          ) : (
            <Button 
              type="primary"
              onClick={handleSubmit}
              disabled={!canProceed()}
              loading={loading}style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
              }}
            >
              {editing ? 'Update Alert' : 'Create Alert'}
            </Button>
          )}
        </Space>
      </div>
    </Modal>
  );
};

export default ClairAIAlertCreator;
